import { useState, useEffect } from "react";
import axios from "axios";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkOrderForm = ({ open, onOpenChange, workOrder, isEditing, onSuccess, existingEngineers = [] }) => {
  const { user: currentUser } = useAuth();
  const canSetAmount = currentUser?.role === "finance" || currentUser?.role === "manager";
  const [formData, setFormData] = useState({
    description: "",
    site_name: "",
    engineer_name: "",
    engineer_email: "",
    is_chargeable: true,
    amount: "",
    priority: "P3",
    status: "Pending",
    job_done_remark: "",
  });
  const [loading, setLoading] = useState(false);
  const [engineerDirectory, setEngineerDirectory] = useState([]);
  const [saveToDirectory, setSaveToDirectory] = useState(false);

  const fetchDirectory = async () => {
    try {
      const res = await axios.get(`${API}/engineer-directory`);
      // Filter out engineers currently unavailable (dates take precedence over manual flag).
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const isCurrentlyUnavailable = (e) => {
        const start = e?.absence_start_date || "";
        const end = e?.absence_end_date || "";
        if (start && end) {
          try {
            const s = new Date(start);
            const en = new Date(end);
            en.setHours(23, 59, 59, 999);
            return today >= s && today <= en;
          } catch {
            return false;
          }
        }
        return e?.is_available === false;
      };
      setEngineerDirectory(res.data.filter((e) => !isCurrentlyUnavailable(e)));
    } catch (e) {
      console.error("Failed to fetch engineer directory", e);
    }
  };

  useEffect(() => {
    if (open) fetchDirectory();
  }, [open]);

  useEffect(() => {
    if (workOrder && isEditing) {
      setFormData({
        description: workOrder.description || "",
        site_name: workOrder.site_name || "",
        engineer_name: workOrder.engineer_name || "",
        engineer_email: workOrder.engineer_email || "",
        is_chargeable: workOrder.is_chargeable !== false,
        amount: workOrder.amount != null ? String(workOrder.amount) : "",
        priority: workOrder.priority || "P3",
        status: workOrder.status || "Pending",
        job_done_remark: workOrder.job_done_remark || "",
      });
    } else {
      setFormData({
        description: "",
        site_name: "",
        engineer_name: "",
        engineer_email: "",
        is_chargeable: true,
        amount: "",
        priority: "P3",
        status: "Pending",
        job_done_remark: "",
      });
    }
    setSaveToDirectory(false);
  }, [workOrder, isEditing, open]);

  const handleSelectSavedEngineer = (engineerId) => {
    if (engineerId === "__manual__") {
      // clear to allow manual entry
      setFormData((prev) => ({ ...prev, engineer_name: "", engineer_email: "" }));
      return;
    }
    const eng = engineerDirectory.find((e) => e.id === engineerId);
    if (eng) {
      setFormData((prev) => ({ ...prev, engineer_name: eng.name, engineer_email: eng.email }));
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Coerce amount to number (or null) before sending
      const amountNum = formData.amount === "" || formData.amount == null
        ? null
        : Number(formData.amount);
      const payload = {
        ...formData,
        amount: Number.isFinite(amountNum) ? amountNum : null,
      };
      const hasNewEngineer = payload.engineer_name && payload.engineer_email;

      if (isEditing && workOrder) {
        await axios.put(`${API}/work-orders/${workOrder.id}`, payload);
        toast.success("Work order updated successfully");
      } else {
        await axios.post(`${API}/work-orders`, payload);
        toast.success("Work order created successfully");
      }

      // Save engineer to directory if requested
      if (saveToDirectory && hasNewEngineer) {
        try {
          await axios.post(`${API}/engineer-directory`, {
            name: payload.engineer_name,
            email: payload.engineer_email,
          });
        } catch (e) {
          console.warn("Could not save engineer to directory", e);
        }
      }

      // Notify user about email if engineer has email
      if (hasNewEngineer) {
        toast.info(`Assignment email queued to ${payload.engineer_email}`, {
          duration: 4000,
        });
      }

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving work order:", error);
      toast.error("Failed to save work order");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-[#E4E4E7] rounded-sm" data-testid="work-order-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            {isEditing ? "Edit Work Order" : "Create New Work Order"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="site_name" className="text-sm font-medium text-[#09090B]">
                Site Name *
              </Label>
              <Input
                id="site_name"
                value={formData.site_name}
                onChange={(e) => handleChange("site_name", e.target.value)}
                placeholder="Enter site name"
                required
                className="border-[#E4E4E7] rounded-sm"
                data-testid="site-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="saved-engineer" className="text-sm font-medium text-[#09090B]">
                Pick Saved Engineer
              </Label>
              <Select onValueChange={handleSelectSavedEngineer} value="">
                <SelectTrigger className="border-[#E4E4E7] rounded-sm" data-testid="saved-engineer-select">
                  <SelectValue placeholder={engineerDirectory.length === 0 ? "No saved engineers yet" : "Select from directory..."} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__manual__" data-testid="engineer-manual-entry">-- Enter manually --</SelectItem>
                  {engineerDirectory.map((eng) => (
                    <SelectItem key={eng.id} value={eng.id} data-testid={`saved-engineer-${eng.id}`}>
                      {eng.name} <span className="text-[#71717A] ml-1">({eng.email})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="engineer_name" className="text-sm font-medium text-[#09090B]">
                Engineer Name
              </Label>
              <Input
                id="engineer_name"
                value={formData.engineer_name}
                onChange={(e) => handleChange("engineer_name", e.target.value)}
                placeholder="Enter engineer name"
                list="engineers-datalist"
                className="border-[#E4E4E7] rounded-sm"
                data-testid="engineer-name-input"
                autoComplete="off"
              />
              <datalist id="engineers-datalist">
                {existingEngineers.map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            </div>

            <div className="space-y-2">
              <Label htmlFor="engineer_email" className="text-sm font-medium text-[#09090B]">
                Engineer Email
                <span className="text-xs text-[#71717A] ml-2 font-normal">(sends assignment email)</span>
              </Label>
              <Input
                id="engineer_email"
                type="email"
                value={formData.engineer_email}
                onChange={(e) => handleChange("engineer_email", e.target.value)}
                placeholder="engineer@company.com"
                className="border-[#E4E4E7] rounded-sm"
                data-testid="engineer-email-input"
                autoComplete="off"
              />
            </div>
          </div>

          {formData.engineer_name && formData.engineer_email && !engineerDirectory.some((e) => e.email === formData.engineer_email.toLowerCase()) && (
            <label className="flex items-center gap-2 text-sm text-[#09090B] cursor-pointer" data-testid="save-to-directory-label">
              <input
                type="checkbox"
                checked={saveToDirectory}
                onChange={(e) => setSaveToDirectory(e.target.checked)}
                className="w-4 h-4 rounded-sm border-[#E4E4E7]"
                data-testid="save-to-directory-checkbox"
              />
              Save "{formData.engineer_name}" to Engineer Directory for future assignments
            </label>
          )}

          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium text-[#09090B]">
              Description *
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Enter work order description"
              required
              rows={4}
              className="border-[#E4E4E7] rounded-sm resize-none"
              data-testid="description-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="priority" className="text-sm font-medium text-[#09090B]">
                Priority *
              </Label>
              <Select value={formData.priority} onValueChange={(value) => handleChange("priority", value)}>
                <SelectTrigger className="border-[#E4E4E7] rounded-sm" data-testid="priority-select">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="P1">P1 (30 days)</SelectItem>
                  <SelectItem value="P2">P2 (20 days)</SelectItem>
                  <SelectItem value="P3">P3 (48 hours)</SelectItem>
                  <SelectItem value="P4">P4 (24 hours)</SelectItem>
                  <SelectItem value="P5">P5 (4 hours)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status" className="text-sm font-medium text-[#09090B]">
                Status *
              </Label>
              <Select value={formData.status} onValueChange={(value) => handleChange("status", value)}>
                <SelectTrigger className="border-[#E4E4E7] rounded-sm" data-testid="status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Assigned">Assigned</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="On Hold">On Hold</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="chargeable" className="text-sm font-medium text-[#09090B]">
                Billing *
              </Label>
              <Select
                value={formData.is_chargeable ? "chargeable" : "non_chargeable"}
                onValueChange={(value) => handleChange("is_chargeable", value === "chargeable")}
              >
                <SelectTrigger className="border-[#E4E4E7] rounded-sm" data-testid="chargeable-select">
                  <SelectValue placeholder="Select billing" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="chargeable" data-testid="chargeable-option">Chargeable</SelectItem>
                  <SelectItem value="non_chargeable" data-testid="non-chargeable-option">Non-Chargeable</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {formData.is_chargeable && canSetAmount && (
            <div className="space-y-2">
              <Label htmlFor="amount" className="text-sm font-medium text-[#09090B]">
                Amount (RM) <span className="text-[#71717A] font-normal">— optional</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A] text-sm font-semibold">RM</span>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => handleChange("amount", e.target.value)}
                  placeholder="0.00"
                  className="pl-10 border-[#E4E4E7] rounded-sm"
                  data-testid="amount-input"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="job_done_remark" className="text-sm font-medium text-[#09090B]">
              Job Done Remark
              <span className="text-xs text-[#71717A] ml-2 font-normal">(Completion notes / work performed)</span>
            </Label>
            <Textarea
              id="job_done_remark"
              value={formData.job_done_remark}
              onChange={(e) => handleChange("job_done_remark", e.target.value)}
              placeholder="Describe work completed, parts replaced, findings, or next actions..."
              rows={3}
              className="border-[#E4E4E7] rounded-sm resize-none"
              data-testid="job-done-remark-input"
            />
          </div>

          <div className="text-sm text-[#71717A] bg-[#F4F4F5] p-3 rounded-sm border border-[#E4E4E7]">
            <strong>Note:</strong> Start date/time will be automatically set to the current time when the work order is created. End date/time will be calculated based on the selected priority.
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-[#E4E4E7] rounded-sm"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-sm transition-colors duration-200"
              data-testid="submit-btn"
            >
              {loading ? "Saving..." : isEditing ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WorkOrderForm;